// packages/agent-kit/src/useTools.ts
import useSWR from 'swr';
import { useCallback } from 'react';
import { toolService, isApiError } from '@dopomogai/api-client';
import type { ToolResponse, UUID } from '@dopomogai/types';
import { AgentKitError } from '../types';

const TOOLS_CACHE_KEY = 'dopomogai/tools/list';

const toolsFetcher = async (): Promise<ToolResponse[]> => {
  const response = await toolService.getAllTools();
  if (isApiError(response)) {
    throw new AgentKitError('Failed to fetch tools.', response);
  }
  return response;
};

/**
 * A hook to fetch and manage the list of all available tools.
 */
export const useTools = () => {
  const { data: tools, error, isLoading, mutate } = useSWR<ToolResponse[], AgentKitError>(
    TOOLS_CACHE_KEY,
    toolsFetcher
  );

  /**
   * Toggles the enabled status of a specific tool.
   */
  const toggleToolStatus = useCallback(async (toolId: UUID, currentStatus: boolean) => {
    // Optimistic UI update for a snappy experience
    if (tools) {
      const updatedTools = tools.map(t =>
        t.id === toolId ? { ...t, enabled: !currentStatus } : t
      );
      mutate(updatedTools, false); // Update local data, but don't re-fetch yet
    }

    const apiCall = currentStatus ? toolService.disableTool : toolService.enableTool;
    const response = await apiCall(toolId);

    if (isApiError(response)) {
      // If API call fails, revert the optimistic update
      mutate(tools, false); 
      throw new AgentKitError(`Failed to ${currentStatus ? 'disable' : 'enable'} tool.`, response);
    }
    
    // Trigger a revalidation to get the canonical state from the server
    mutate();
    return response;
  }, [tools, mutate]);

  /**
   * Permanently deletes a tool.
   */
  const deleteTool = useCallback(async (toolId: UUID) => {
    // Optimistic UI update
    if (tools) {
      const updatedTools = tools.filter(t => t.id !== toolId);
      mutate(updatedTools, false);
    }

    const response = await toolService.deleteTool(toolId);

    if (isApiError(response)) {
      mutate(tools, false); // Revert on failure
      throw new AgentKitError('Failed to delete tool.', response);
    }

    // No need to re-fetch on success, the optimistic update is correct.
    // We can trigger a revalidation just in case, but it's often not necessary for deletes.
    // mutate();
    return response;
  }, [tools, mutate]);

  return {
    tools,
    isLoading,
    error,
    toggleToolStatus,
    deleteTool,
    mutate, // Expose mutate for manual re-fetching if needed
  };
};